import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory } from 'react-router-dom';

import { useSelector, useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { uploadLeadsDetailsSaga } from './saga';
import {
  emailsGridActions,
  reducer,
  sliceKey,
} from 'app/pages/EmailsGridPage/slice';
import {
  selectLastRefreshTrigger,
  selectEmailTemplatesLoading,
  selectGenerateTemplatesLoading,
  selectGenerateTemplates,
  selectGenerateInProgress,
  selectGenerateStartedDealerId,
  selectGenerateProgress,
} from 'app/pages/EmailsGridPage/selectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import DownloadModalMAIQ from 'app/components/DownloadModalMAIQ';
import { useStyles } from 'app/pages/EmailsGridPage/style';
import {
  DataTableContainer,
  PageContainer,
  PageHeader,
  DataTableHeaderActions,
} from 'app/components/StyledComponents';
import { BreadcrumbsComponent } from 'app/components/BreadcrumbsComponent';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import FilterTrayMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import ActionDropdownMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/actionDropdown/ActionDropdownMAIQ';
import { DeleteConfirmationDialog } from 'app/components/ReusableUIComponents/DeleteConfirmationDialog';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { PreferencesModal } from 'app/pages/EmailsGridPage/components/PreferencesModal';
import { useSegmentInventories } from 'app/pages/EmailsGridPage/hooks/useSegmentInventories';
import DataGridTypes from 'types/DataGridTypes';
import { useEmailsGridSocket } from 'app/pages/EmailsGridPage/hooks/useEmailsGridSocket';
import { useEmailTemplatesFilterTray } from 'app/pages/EmailsGridPage/hooks/useEmailTemplatesFilterTray';
import { useLastInventorySyncDate } from 'app/pages/EmailsGridPage/hooks/useLastInventorySyncDate';
import { usePendingApprovalTemplatesCount } from 'app/pages/EmailsGridPage/hooks/usePendingApprovalTemplatesCount';
import {
  buildHumanInTheLoopPath,
  parseSegmentNameFromSearch,
} from 'app/pages/HumanInTheLoopPage/utils/humanInTheLoopRouteUtils';
import { buildTestNurturingPath } from 'app/pages/TestNurturingPage/utils/testNurturingRouteUtils';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
import { EmailTemplatePreviewDialog } from 'app/NewAIAvatarLayout/sharedComponents/EmailTemplatePreviewDialog';
import {
  saveGenerationState,
  loadGenerationState,
  clearGenerationState as clearLocalStorageGenerationState,
  updateGenerationProgress,
  updateSocketMessages,
} from 'app/pages/EmailsGridPage/utils/generationPersistence';
import { formatGenerationErrors } from 'app/pages/EmailsGridPage/utils/generationErrorFormatter';

import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import Popover from '@mui/material/Popover';
import Dialog from '@mui/material/Dialog';
import Portal from '@mui/material/Portal';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import FilterListIcon from '@mui/icons-material/FilterList';

type EmailsGridPageProps = {
  hideGenerateEmailTemplateDropdown?: boolean;
  defaultGeneratedTypeStatic?: boolean;
  breadcrumbsList?: { label: string; link?: string; isActive?: boolean }[];
  pageHeaderTitle?: string;
  /** No PageContainer / Helmet / breadcrumbs / title — grid + filters + modals only (e.g. Test Nurturing page). */
  embedded?: boolean;
  /** When set, Create Template / Edit navigate with this parent crumb (default is All Email Templates → /emails-grid). */
  emailTemplateBreadcrumbOrigin?: { label: string; path: string };
  /**
   * `testNurturingGenerated`: {@link DataGridTypes.TEST_GENERATED_TEMPLATES} + Quick Filters default `generated_type` = dynamic.
   * `hilPendingApproval`: same Email Templates grid, but status is controlled from Quick Filters (default `PENDING_APPROVAL`).
   * `staticValueMessages`: Configure static value messages — STATUS chip + sort by expiration_date.
   */
  emailTemplatesGridPreset?:
    | 'default'
    | 'testNurturingGenerated'
    | 'hilPendingApproval'
    | 'staticValueMessages';
  /** Syncs server totalDocuments to parent (e.g. HIL summary card count). */
  onTotalDocumentsChange?: (totalDocuments: number) => void;
};

export function EmailsGridPage({
  hideGenerateEmailTemplateDropdown = false,
  defaultGeneratedTypeStatic = false,
  breadcrumbsList,
  pageHeaderTitle = 'All Email Templates',
  embedded = false,
  emailTemplateBreadcrumbOrigin,
  emailTemplatesGridPreset = 'default',
  onTotalDocumentsChange,
}: EmailsGridPageProps) {
  const classes = useStyles();

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: uploadLeadsDetailsSaga });

  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();

  const hilSegmentNameFromUrl = React.useMemo(
    () =>
      emailTemplatesGridPreset === 'hilPendingApproval'
        ? parseSegmentNameFromSearch(history.location.search)
        : '',
    [emailTemplatesGridPreset, history.location.search],
  );

  const emailTemplateParentNavState = emailTemplateBreadcrumbOrigin
    ? {
        breadcrumbParentLabel: emailTemplateBreadcrumbOrigin.label,
        breadcrumbParentPath: emailTemplateBreadcrumbOrigin.path,
      }
    : undefined;
  const lastRefreshTrigger = useSelector(selectLastRefreshTrigger);
  const emailTemplatesLoading = useSelector(selectEmailTemplatesLoading);
  const generateTemplatesLoading = useSelector(selectGenerateTemplatesLoading);
  const generateTemplates = useSelector(selectGenerateTemplates);
  const generateInProgressFromRedux = useSelector(selectGenerateInProgress);
  const generateStartedDealerIdFromRedux = useSelector(selectGenerateStartedDealerId);
  const generateProgressFromRedux = useSelector(selectGenerateProgress);
  const [gridRefreshKey, setGridRefreshKey] = React.useState(0);

  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer.dealer || '';
  const isTestNurturingGeneratedPreset =
    emailTemplatesGridPreset === 'testNurturingGenerated';
  const isHilPendingApprovalPreset = emailTemplatesGridPreset === 'hilPendingApproval';
  const isStatusQuickFilterPreset =
    isTestNurturingGeneratedPreset || isHilPendingApprovalPreset;

  const refreshGrid = React.useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['GET', 'api/v2/templates'],
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: ['hil-templates-pending-count', selectedDealerId],
      exact: true,
    });
    setGridRefreshKey((k) => k + 1);
  }, [queryClient, selectedDealerId]);

  const dispatchSetGenerateCompleted = React.useCallback(() => {
    dispatch(emailsGridActions.setGenerateCompleted());
  }, [dispatch]);

  const dispatchSetGenerateProgress = React.useCallback((progress: { current: number; total: number }) => {
    dispatch(emailsGridActions.setGenerateProgress(progress));
    // Also save to localStorage for persistence
    if (selectedDealerId) {
      updateGenerationProgress(selectedDealerId, progress);
    }
  }, [dispatch, selectedDealerId]);

  const persistSocketMessage = React.useCallback((message: any) => {
    if (selectedDealerId) {
      updateSocketMessages(selectedDealerId, message);
    }
  }, [selectedDealerId]);

  // Load initial socket messages from localStorage for current dealer
  const initialSocketMessages = React.useMemo(() => {
    if (!selectedDealerId) return [];
    const persistedState = loadGenerationState(selectedDealerId);
    return persistedState?.socketMessages || [];
  }, [selectedDealerId]);

  // Socket: connect on load, subscribe by dealer; on completion dispatch Redux so state persists across route/dealer change
  const {
    socketMessages,
    generationCompleted,
    completionResult,
    progressFromSocket,
    clearGenerationState,
  } = useEmailsGridSocket(
    selectedDealerId,
    refreshGrid,
    dispatchSetGenerateCompleted,
    dispatchSetGenerateProgress,
    persistSocketMessage,
    generateProgressFromRedux,
    initialSocketMessages,
  );

  // When status/update succeeds, refresh grid by invalidating cache and remounting.
  // Skip the initial mount: `lastRefreshTrigger` is a global Redux counter that may
  // already be > 0 from a previous page's mutation (e.g. approve/reject on HIL before
  // navigating here via breadcrumb). Reacting to it on mount would aggressively remove
  // queries that are mid-flight on this fresh page and cause cancellation errors.
  const previousRefreshTriggerRef = React.useRef(lastRefreshTrigger);
  React.useEffect(() => {
    if (lastRefreshTrigger === previousRefreshTriggerRef.current) return;
    previousRefreshTriggerRef.current = lastRefreshTrigger;
    void queryClient.invalidateQueries({
      queryKey: ['GET', 'api/v2/templates'],
      exact: false,
    });
    void queryClient.invalidateQueries({
      queryKey: ['hil-templates-pending-count', selectedDealerId],
      exact: true,
    });
    setGridRefreshKey((k) => k + 1);
  }, [lastRefreshTrigger, queryClient, selectedDealerId]);

  const [anchorElPopover, setAnchorElPopover] =
    React.useState<HTMLButtonElement | null>(null);
  const [popoverMessage, setPopoverMessage] = React.useState<any>('');
  const openPopover = Boolean(anchorElPopover);
  const popoverId = openPopover ? 'simple-popover' : undefined;
  const [openTemplatePreviewDialog, setOpenTemplatePreviewDialog] =
    React.useState(false);
  const [previewTemplateId, setPreviewTemplateId] = React.useState('');
  const [openDownloadModal, setOpenDownloadModal] = React.useState(false);
  const [downloadUrl, setDownloadUrl] = React.useState('');
  const [downloadFileName, setDownloadFileName] = React.useState('');
  /** Selected row _ids from grid checkbox selection (for bulk actions) */
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  /** True if any selected template is archived – Expire action should be disabled in that case */
  const [hasArchivedInSelection, setHasArchivedInSelection] = React.useState(false);
  /** Generate Email Templates: progress card visible when generating */
  const [showGenerateProgressPopup, setShowGenerateProgressPopup] =
    React.useState(false);
  /** Socket details modal (messages + completion + refresh) – opened when user clicks progress card */
  const [showSocketDetailsModal, setShowSocketDetailsModal] =
    React.useState(false);
  /** Standalone completion result modal – shown when generation completes, independent of progress card */
  const [showCompletionResultModal, setShowCompletionResultModal] =
    React.useState(false);
  /** Generate button: disabled + spinner from click until template_generation_completed */
  const [isGenerateInProgress, setIsGenerateInProgress] = React.useState(false);
  /** Status filter for grid: show Active and/or Archived. Default both. */
  const [statusFilter, setStatusFilter] = React.useState({
    showActive: true,
    showArchived: false,
  });

  /** Filter tray (metadata filters): open state and anchor for popover. */
  const [filterTrayOpen, setFilterTrayOpen] = React.useState(false);
  const [filterTrayAnchorEl, setFilterTrayAnchorEl] =
    React.useState<HTMLElement | null>(null);
  /** Delete confirmation dialog for bulk delete */
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [isBulkDeleteInProgress, setIsBulkDeleteInProgress] = React.useState(false);
  /** Preferences modal (segment inventories / VINs) */
  const [preferencesModalOpen, setPreferencesModalOpen] = React.useState(false);

  const lastInventorySyncDateStatus = useLastInventorySyncDate(selectedDealerId);
  const pendingApprovalTemplatesStatus = usePendingApprovalTemplatesCount(
    isTestNurturingGeneratedPreset ? selectedDealerId : undefined,
  );

  const {
    segmentRows,
    headerRow,
    loading: segmentInventoriesLoading,
    error: segmentInventoriesError,
    canSavePreferences,
    expandedCell,
    vehiclesForExpandedCell,
    vehiclesLoading: vehiclesForCellLoading,
    vehiclesError: vehiclesForCellError,
    preferredVinsForExpandedCell,
    missingVinsForExpandedCell,
    togglePreferredVin,
    savePreferences,
    saveLoading: preferencesSaveLoading,
    toggleExpanded,
  } = useSegmentInventories(selectedDealerId, preferencesModalOpen);

  const handleOpenHilTemplatesPending = React.useCallback(() => {
    history.push(buildHumanInTheLoopPath('templates_pending'), {
      breadcrumbParentLabel: 'Test nurturing',
      breadcrumbParentPath: buildTestNurturingPath('templates'),
    });
  }, [history]);

  const handleSavePreferences = React.useCallback(async () => {
    try {
      await savePreferences();
      setPreferencesModalOpen(false);
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Preferences saved successfully',
        }),
      );
    } catch (err: any) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: err?.message || 'Failed to save preferences',
        }),
      );
    }
  }, [savePreferences, dispatch]);

  const {
    categories: filterTrayCategories,
    optionsForActiveCategory: filterTrayOptions,
    selectedByCategory: filterTraySelected,
    onSelectionChange: filterTrayOnSelectionChange,
    onClearAllFilters: filterTrayOnClearAll,
    onClearActiveCategory: filterTrayOnClearActive,
    onClearAllInPanel: filterTrayOnClearAllInPanel,
    metadataFilters,
    totalActiveCount: filterTrayTotalActive,
    activeCategoryId: filterTrayActiveCategoryId,
    setActiveCategoryId: filterTraySetActiveCategoryId,
    isLoading: filterTrayIsLoading,
  } = useEmailTemplatesFilterTray({
    dealerId: selectedDealerId,
    statusFilter: isStatusQuickFilterPreset ? undefined : statusFilter,
    includeStatusField: isStatusQuickFilterPreset,
    fetchEnabled: filterTrayOpen,
    initialSelectedByCategory: defaultGeneratedTypeStatic
      ? { generated_type: ['static'] }
      : isTestNurturingGeneratedPreset
        ? { generated_type: ['dynamic'] }
        : isHilPendingApprovalPreset
          ? {
              status: ['PENDING_APPROVAL'],
              ...(hilSegmentNameFromUrl
                ? { segment_name: [hilSegmentNameFromUrl] }
                : {}),
            }
        : undefined,
  });

  const effectiveMetadataFilters = React.useMemo(
    () =>
      defaultGeneratedTypeStatic
        ? { ...metadataFilters, generated_type: ['static'] }
        : metadataFilters,
    [defaultGeneratedTypeStatic, metadataFilters],
  );

  const metadataFiltersKey = React.useMemo(
    () => JSON.stringify(effectiveMetadataFilters),
    [effectiveMetadataFilters],
  );

  const emailTemplatesGridType = React.useMemo(() => {
    if (isTestNurturingGeneratedPreset) {
      return DataGridTypes.TEST_GENERATED_TEMPLATES(
        selectedDealerId,
        selectedDealer?.site_title,
        statusFilter,
        effectiveMetadataFilters,
      );
    }
    if (isHilPendingApprovalPreset) {
      return DataGridTypes.HIL_PENDING_APPROVAL_TEMPLATES(
        selectedDealerId,
        selectedDealer?.site_title,
        undefined,
        effectiveMetadataFilters,
      );
    }
    if (emailTemplatesGridPreset === 'staticValueMessages') {
      return DataGridTypes.STATIC_CONTENT_VALUE_MESSAGES_TEMPLATES(
        selectedDealerId,
        selectedDealer?.site_title,
        statusFilter,
        effectiveMetadataFilters,
      );
    }
    return DataGridTypes.EMAIL_TEMPLATES(
      selectedDealerId,
      selectedDealer?.site_title,
      isStatusQuickFilterPreset ? undefined : statusFilter,
      effectiveMetadataFilters,
    );
  }, [
    effectiveMetadataFilters,
    isHilPendingApprovalPreset,
    isStatusQuickFilterPreset,
    isTestNurturingGeneratedPreset,
    selectedDealer?.site_title,
    selectedDealerId,
    statusFilter,
  ]);

  /** Drives progress card % from socket current/total; card expects section-by-section fills so we replay 1..current */
  const [simulatedFilledCount, setSimulatedFilledCount] = React.useState(0);

  // Reset simulated count when generation is cleared (progressFromSocket reset to 0/1)
  React.useEffect(() => {
    if (progressFromSocket.current === 0 && progressFromSocket.total === 1) {
      setSimulatedFilledCount(0);
    }
  }, [progressFromSocket.current, progressFromSocket.total]);

  // Replay section fills so card's progress = (current/total)*100 without touching the card component
  React.useEffect(() => {
    const target = progressFromSocket.current;
    const total = Math.max(progressFromSocket.total, 1);
    if (target <= simulatedFilledCount || target > total) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = simulatedFilledCount; i < target; i++) {
      timers.push(
        setTimeout(() => setSimulatedFilledCount(i + 1), (i - simulatedFilledCount) * 40),
      );
    }
    return () => timers.forEach(t => clearTimeout(t));
  }, [progressFromSocket.current, progressFromSocket.total, simulatedFilledCount]);

  const totalSections = Math.max(progressFromSocket.total, 1);
  /** Card expects sections + text with key.index (multi-word value) to drive progress %; we send current/total as section fills so % = (simulatedFilledCount/total)*100 */
  const streamingDataForCard = React.useMemo(
    () => ({
      message: {
        sections: Array.from({ length: totalSections }, (_, i) => ({
          index: i + 1,
          main: `Step ${i + 1}`,
          sub_text: '',
        })),
        ...(simulatedFilledCount >= 1 && {
          text: {
            key: { index: simulatedFilledCount },
            value: `Step ${simulatedFilledCount} of ${totalSections} completed.`,
          },
        }),
      },
    }),
    [totalSections, simulatedFilledCount],
  );

  const currentSectionForCard =
    showGenerateProgressPopup && totalSections >= 1
      ? {
          index: Math.min(simulatedFilledCount || 1, totalSections),
          type: 'step' as const,
          main: 'Generating...',
          sub_text: `Creating email templates (${simulatedFilledCount}/${totalSections})`,
        }
      : undefined;

  // Load generation state from localStorage on mount or dealer change (primary source of truth)
  React.useEffect(() => {
    if (!selectedDealerId) return;

    const persistedState = loadGenerationState(selectedDealerId);
    
    if (persistedState && persistedState.generateInProgress) {
      // Restore UI state from localStorage
      setIsGenerateInProgress(true);
      setShowGenerateProgressPopup(true);
      
      // Restore simulated filled count so progress card shows correct state immediately
      if (persistedState.progress.current > 0) {
        setSimulatedFilledCount(persistedState.progress.current);
      }
      
      // Sync Redux with localStorage
      if (!generateInProgressFromRedux || generateStartedDealerIdFromRedux !== selectedDealerId) {
        dispatch(
          emailsGridActions.requestGenerateEmailTemplates({ dealerId: selectedDealerId })
        );
      }
      if (
        persistedState.progress.current !== generateProgressFromRedux.current ||
        persistedState.progress.total !== generateProgressFromRedux.total
      ) {
        dispatch(emailsGridActions.setGenerateProgress(persistedState.progress));
      }
    } else {
      // No persisted state for this dealer - ensure UI is clean
      if (isGenerateInProgress && generateStartedDealerIdFromRedux !== selectedDealerId) {
        setIsGenerateInProgress(false);
        setShowGenerateProgressPopup(false);
        setSimulatedFilledCount(0);
      }
    }
  }, [selectedDealerId]); // Only run on dealer change or mount

  // Restore generate UI state when coming back to page (e.g. after navigation) – Redux persisted it. Do NOT restore when completion already received.
  React.useEffect(() => {
    if (
      generationCompleted ||
      !generateInProgressFromRedux ||
      generateStartedDealerIdFromRedux !== selectedDealerId ||
      isGenerateInProgress
    ) {
      return;
    }
    setIsGenerateInProgress(true);
    setShowGenerateProgressPopup(true);
  }, [
    generationCompleted,
    generateInProgressFromRedux,
    generateStartedDealerIdFromRedux,
    selectedDealerId,
    isGenerateInProgress,
  ]);

  // When a new generation is dispatched externally (e.g. from Test Template Generation modal) while a previous
  // run's completion modal is still open, clear stale socket-completion state so the fresh run can take over
  // the progress card and details modal exactly like the local Generate dropdown flow does.
  const prevGenerateInProgressRef = React.useRef(generateInProgressFromRedux);
  React.useEffect(() => {
    const transitionedToInProgress =
      !prevGenerateInProgressRef.current && generateInProgressFromRedux;
    prevGenerateInProgressRef.current = generateInProgressFromRedux;
    if (!transitionedToInProgress) return;
    if (generationCompleted || showCompletionResultModal) {
      clearGenerationState();
      setShowCompletionResultModal(false);
    }
  }, [
    generateInProgressFromRedux,
    generationCompleted,
    showCompletionResultModal,
    clearGenerationState,
  ]);

  // When dealer changes, clear in-progress UI state so new dealer has clean slate
  // BUT keep localStorage intact for each dealer so we can restore when switching back
  React.useEffect(() => {
    if (
      generateStartedDealerIdFromRedux != null &&
      generateStartedDealerIdFromRedux !== selectedDealerId
    ) {
      dispatch(emailsGridActions.setGenerateCompleted());
      setIsGenerateInProgress(false);
      setShowGenerateProgressPopup(false);
      
      // DO NOT clear localStorage here - we want each dealer's state to persist
      // localStorage will be cleared only when generation completes or user dismisses
    }
  }, [selectedDealerId, generateStartedDealerIdFromRedux, dispatch]);

  // Hide progress card, close details modal, re-enable Generate button, and open standalone completion modal when template generation is completed
  React.useEffect(() => {
    if (generationCompleted) {
      setShowGenerateProgressPopup(false);
      setShowSocketDetailsModal(false);
      setIsGenerateInProgress(false);
      setShowCompletionResultModal(true);
      
      // Clear localStorage for this dealer
      if (selectedDealerId) {
        clearLocalStorageGenerationState(selectedDealerId);
      }
    }
  }, [generationCompleted, selectedDealerId]);

  // Re-enable button on API error so user can retry
  React.useEffect(() => {
    if (generateTemplates?.error && isGenerateInProgress) {
      setIsGenerateInProgress(false);
    }
  }, [generateTemplates?.error, isGenerateInProgress]);

  const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      setAnchorElPopover(event.currentTarget);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClosePopover = () => {
    setAnchorElPopover(null);
    setPopoverMessage('');
  };

  const handleRowClick = (row: any) => {
    try {
      if (isHilPendingApprovalPreset) return;
      if (row?._id) {
        setPreviewTemplateId(row._id);
        setOpenTemplatePreviewDialog(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleCellClick = (cell: any, clickedElement?: string, extraData?: any) => {
    try {
      const row = cell?.row;
      if (cell?.field === 'name' && !isHilPendingApprovalPreset) {
        if (row?._id) {
          setPreviewTemplateId(row._id);
          setOpenTemplatePreviewDialog(true);
        }
      } else if (clickedElement === 'status_change' && row?._id && extraData) {
        dispatch(
          emailsGridActions.requestUpdateEmailTemplate({
            dealerId: selectedDealerId,
            emailTemplateId: row._id,
            data: { status: extraData },
          }),
        );
      } else if (cell?.field === 'action' && row && clickedElement) {
        if (clickedElement === 'edit') {
          history.push(
            `/emails-edit/${row._id}`,
            emailTemplateParentNavState,
          );
        } else if (clickedElement === 'preview') {
          setPreviewTemplateId(row._id);
          setOpenTemplatePreviewDialog(true);
        } else if (clickedElement === 'approve') {
          dispatch(
            emailsGridActions.requestApproveEmailTemplate({
              dealerId: selectedDealerId,
              emailTemplateId: row._id,
            }),
          );
        } else if (clickedElement === 'reject') {
          dispatch(
            emailsGridActions.requestRejectEmailTemplate({
              dealerId: selectedDealerId,
              emailTemplateId: row._id,
            }),
          );
        } else if (clickedElement === 'delete') {
          setSelectedRowIds([row._id]);
          setDeleteDialogOpen(true);
        } else if (clickedElement === 'download') {
          handleDownloadEmailTemplate(row._id, row?.name);
        } else if (clickedElement === 'expire') {
          handleExpireEmailTemplate(row);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  /** Single template expire – calls DELETE /api/v2/templates/expire with this row's id. */
  const handleExpireEmailTemplate = (row: { _id?: string; name?: string }) => {
    try {
      if (!row?._id || !selectedDealerId) return;
      dispatch(
        emailsGridActions.requestExpireEmailTemplates({
          dealerId: selectedDealerId,
          ids: [row._id],
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: 'Failed to expire template',
        }),
      );
    }
  };

  const handleStatusFilterChange = React.useCallback(
    (which: 'showActive' | 'showArchived') => {
      setStatusFilter((prev) => {
        const next = { ...prev, [which]: !prev[which] };
        if (!next.showActive && !next.showArchived) {
          return prev;
        }
        return next;
      });
      void queryClient.invalidateQueries({
        queryKey: ['GET', 'api/v2/templates'],
        exact: false,
      });
      setGridRefreshKey((k) => k + 1);
    },
    [queryClient],
  );

  const handleGenerateEmailTemplates = React.useCallback(() => {
    if (!selectedDealerId || isGenerateInProgress) return;
    clearGenerationState();
    dispatch(emailsGridActions.setGenerateProgress({ current: 0, total: 1 }));
    setIsGenerateInProgress(true);
    dispatch(
      (emailsGridActions.requestGenerateEmailTemplates as (payload: {
        dealerId: string;
      }) => any)({ dealerId: selectedDealerId }),
    );
    setShowGenerateProgressPopup(true);
    
    // Save initial state to localStorage
    saveGenerationState(selectedDealerId, {
      generateInProgress: true,
      progress: { current: 0, total: 1 },
      socketMessages: [],
      lastSocketMessage: null,
    });
  }, [selectedDealerId, isGenerateInProgress, dispatch, clearGenerationState]);

  /** Bulk delete – calls DELETE /api/v2/templates/:id for each selected row. */
  const handleBulkDelete = React.useCallback(() => {
    if (selectedRowIds.length === 0 || !selectedDealerId) return;
    setIsBulkDeleteInProgress(true);
    dispatch(
      emailsGridActions.requestDeleteEmailTemplates({
        dealerId: selectedDealerId,
        ids: selectedRowIds,
      }),
    );
  }, [selectedRowIds, selectedDealerId, dispatch]);

  React.useEffect(() => {
    if (!isBulkDeleteInProgress || emailTemplatesLoading) return;
    setIsBulkDeleteInProgress(false);
    setSelectedRowIds([]);
    setDeleteDialogOpen(false);
  }, [isBulkDeleteInProgress, emailTemplatesLoading]);

  /** Bulk expire – calls DELETE /api/v2/templates/expire with selected row ids. */
  const handleBulkExpire = React.useCallback(() => {
    if (selectedRowIds.length === 0 || !selectedDealerId) return;
    dispatch(
      emailsGridActions.requestExpireEmailTemplates({
        dealerId: selectedDealerId,
        ids: selectedRowIds,
      }),
    );
    setSelectedRowIds([]);
  }, [selectedRowIds, selectedDealerId, dispatch]);

  const handleBulkApprove = React.useCallback(() => {
    if (selectedRowIds.length === 0 || !selectedDealerId) return;
    dispatch(
      emailsGridActions.requestBulkApproveEmailTemplates({
        dealerId: selectedDealerId,
        ids: selectedRowIds,
      }),
    );
    setSelectedRowIds([]);
  }, [selectedRowIds, selectedDealerId, dispatch]);

  const handleBulkReject = React.useCallback(() => {
    if (selectedRowIds.length === 0 || !selectedDealerId) return;
    dispatch(
      emailsGridActions.requestBulkRejectEmailTemplates({
        dealerId: selectedDealerId,
        ids: selectedRowIds,
      }),
    );
    setSelectedRowIds([]);
  }, [selectedRowIds, selectedDealerId, dispatch]);

  const handleDownloadEmailTemplate = (templateId: string, templateName?: string) => {
    try {
      const name = templateName || `email-template-${templateId}`;
      const cleanTemplateName = name
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);

      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const url = `${baseUrl}/api/v2/templates/download/${templateId}?dealer=${selectedDealerId}`;

      setDownloadUrl(url);
      setDownloadFileName(`${cleanTemplateName}.html`);
      setOpenDownloadModal(true);
    } catch (error) {
      console.error('Error preparing download:', error);
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message: 'Failed to prepare download',
        }),
      );
    }
  };

  const emailsTemplatesMainBody = (
    <>
      {!selectedDealerId ? (
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            className={classes.dealerTypography}
          >
            Please select a dealer
          </Typography>
        ) : (
          <>
            <DataTableContainer sx={{ height: 'auto', minHeight: 0 }}>
              {/* Last Inventory Sync Date – full-width row above actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  minHeight: 40,
                }}
              >
                {isTestNurturingGeneratedPreset && (
                  <Button
                    size="small"
                    variant="outlined"
                    color={pendingApprovalTemplatesStatus.isError ? 'error' : 'warning'}
                    startIcon={<NotificationsActive fontSize="small" />}
                    onClick={handleOpenHilTemplatesPending}
                    sx={{
                      textTransform: 'none',
                      minHeight: 40,
                      borderRadius: 2,
                      px: 1.75,
                    }}
                  >
                    {pendingApprovalTemplatesStatus.isLoading
                      ? 'Pending Approval'
                      : `Pending Approval (${pendingApprovalTemplatesStatus.count})`}
                  </Button>
                )}
                <Paper
                  elevation={0}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'action.hover',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    Last Inventory Sync Date
                  </Typography>
                  {lastInventorySyncDateStatus.isLoading ? (
                    <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: lastInventorySyncDateStatus.isError ? 'error.main' : 'text.primary',
                      }}
                    >
                      {lastInventorySyncDateStatus.displayText}
                    </Typography>
                  )}
                </Paper>
              </Box>

              <DataTableHeaderActions>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                    }}
                  >
                    {!isStatusQuickFilterPreset && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.25 }}>
                          Status:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={statusFilter.showActive}
                              disabled={statusFilter.showActive && !statusFilter.showArchived}
                              onChange={() => handleStatusFilterChange('showActive')}
                            />
                          }
                          label="Active"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={statusFilter.showArchived}
                              disabled={statusFilter.showArchived && !statusFilter.showActive}
                              onChange={() => handleStatusFilterChange('showArchived')}
                            />
                          }
                          label="Archived"
                        />
                      </>
                    )}
                    <Badge
                      badgeContent={filterTrayTotalActive > 0 ? filterTrayTotalActive : undefined}
                      color="primary"
                      sx={{ '& .MuiBadge-badge': { fontSize: 10 }, ml: 0.5 }}
                    >
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        startIcon={<FilterListIcon />}
                        onClick={e => {
                          setFilterTrayAnchorEl(e.currentTarget);
                          setFilterTrayOpen(true);
                        }}
                        sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                      >
                        Quick Filters
                      </Button>
                    </Badge>
                  </Box>
                  <FilterTrayMAIQ
                    isLoading={filterTrayIsLoading}
                    open={filterTrayOpen}
                    onClose={() => {
                      setFilterTrayOpen(false);
                      setFilterTrayAnchorEl(null);
                    }}
                    anchorEl={filterTrayAnchorEl}
                    categories={filterTrayCategories}
                    activeCategoryId={filterTrayActiveCategoryId}
                    onActiveCategoryChange={filterTraySetActiveCategoryId}
                    optionsForActiveCategory={filterTrayOptions}
                    selectedByCategory={filterTraySelected}
                    onSelectionChange={filterTrayOnSelectionChange}
                    totalActiveCount={filterTrayTotalActive}
                    onClearAllFilters={filterTrayOnClearAll}
                    onClearActiveCategory={filterTrayOnClearActive}
                    onClearAllInPanel={filterTrayOnClearAllInPanel}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                  <ActionDropdownMAIQ
                    buttonLabel="Actions"
                    color="primary"
                    ariaLabel="Email templates actions"
                    actions={
                      isHilPendingApprovalPreset
                        ? [
                            {
                              id: 'refresh',
                              label: 'Refresh list',
                              onClick: refreshGrid,
                              disabled: false,
                              icon: <RefreshIcon fontSize="small" />,
                            },
                            {
                              id: 'approve',
                              label: 'Approve',
                              onClick: handleBulkApprove,
                              disabled: selectedRowIds.length === 0,
                              icon: <CheckCircleOutline fontSize="small" />,
                            },
                            {
                              id: 'reject',
                              label: 'Reject',
                              onClick: handleBulkReject,
                              disabled: selectedRowIds.length === 0,
                              icon: <CancelOutlined fontSize="small" />,
                            },
                            {
                              id: 'delete',
                              label: 'Delete',
                              onClick: () => setDeleteDialogOpen(true),
                              disabled: selectedRowIds.length === 0,
                              icon: <DeleteIcon fontSize="small" />,
                            },
                          ]
                        : [
                            {
                              id: 'refresh',
                              label: 'Refresh list',
                              onClick: refreshGrid,
                              disabled: false,
                              icon: <RefreshIcon fontSize="small" />,
                            },
                            {
                              id: 'expire',
                              label: 'Expire',
                              onClick: handleBulkExpire,
                              disabled:
                                selectedRowIds.length === 0 || hasArchivedInSelection,
                              icon: <EventBusyIcon fontSize="small" />,
                            },
                            {
                              id: 'delete',
                              label: 'Delete',
                              onClick: () => setDeleteDialogOpen(true),
                              disabled: selectedRowIds.length === 0,
                              icon: <DeleteIcon fontSize="small" />,
                            },
                          ]
                    }
                  />
                  {!hideGenerateEmailTemplateDropdown && !isHilPendingApprovalPreset && (
                    <ActionDropdownMAIQ
                      buttonLabel="Generate Email Template"
                      color="primary"
                      ariaLabel="Generate email template actions"
                      actions={[
                        {
                          id: 'generate',
                          label: isGenerateInProgress ? 'Generating...' : 'Generate',
                          onClick: handleGenerateEmailTemplates,
                          disabled: isGenerateInProgress,
                          icon: isGenerateInProgress ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <AutoAwesome fontSize="small" />
                          ),
                        },
                        {
                          id: 'preferences',
                          label: 'Preferences',
                          onClick: () => setPreferencesModalOpen(true),
                          disabled: false,
                          icon: <SettingsIcon fontSize="small" />,
                        },
                      ]}
                    />
                  )}
                  {!isHilPendingApprovalPreset && !isTestNurturingGeneratedPreset && (
                    <ButtonMAIQ
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        history.push('/emails-create', emailTemplateParentNavState)
                      }
                    >
                      Create Template
                    </ButtonMAIQ>
                  )}
                </Box>
              </DataTableHeaderActions>

              <DataGridMAIQ
                key={`${selectedDealerId}-${gridRefreshKey}-${emailTemplatesGridPreset}-${statusFilter.showActive}-${statusFilter.showArchived}-${metadataFiltersKey}`}
                type={emailTemplatesGridType}
                onTotalDocumentsChange={onTotalDocumentsChange}
                onRowClick={handleRowClick}
                onCellClick={handleCellClick}
                onMultiSelect={(selectionModel: string[], selectedRows?: any[]) => {
                  const selectedTemplateIds = (selectedRows ?? [])
                    .map((row: any) => row?._id)
                    .filter((id: any): id is string => typeof id === 'string' && id.trim().length > 0);
                  setSelectedRowIds(selectedTemplateIds);
                  const anyArchived =
                    selectedRows?.some(
                      (r: any) =>
                        String(r?.status ?? '').toUpperCase() === 'ARCHIVED',
                    ) ?? false;
                  setHasArchivedInSelection(anyArchived);
                }}
              />
            </DataTableContainer>
          </>
        )}
    </>
  );

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>Emails</title>
          <meta name="description" content="Emails - Myautoiq" />
        </Helmet>
      )}
      {!embedded ? (
        <PageContainer>
          {breadcrumbsList?.length ? (
            <Box sx={{ mb: 1 }}>
              <BreadcrumbsComponent breadcrumbsList={breadcrumbsList} />
            </Box>
          ) : null}
          <PageHeader>{pageHeaderTitle}</PageHeader>
          {emailsTemplatesMainBody}
        </PageContainer>
      ) : (
        emailsTemplatesMainBody
      )}
      <Popover
        id={popoverId}
        open={openPopover}
        anchorEl={anchorElPopover}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Typography sx={{ p: 2 }}>
          {popoverMessage
            .split('.')
            .filter(line => line.trim() !== '')
            .map((line, index) => (
              <React.Fragment key={index}>
                {line.trim()}
                {index < popoverMessage.length - 1 && <br />}
              </React.Fragment>
            ))}
        </Typography>
      </Popover>
      <EmailTemplatePreviewDialog
        open={openTemplatePreviewDialog}
        onClose={() => {
          setOpenTemplatePreviewDialog(false);
          setPreviewTemplateId('');
        }}
        templateId={previewTemplateId || undefined}
        dealerId={selectedDealerId || undefined}
      />
      
      {/* Download Modal */}
      <DownloadModalMAIQ
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        downloadUrl={downloadUrl}
        title="Download Email Template"
        fileName={downloadFileName}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          if (isBulkDeleteInProgress) return;
          setDeleteDialogOpen(false);
        }}
        onConfirm={handleBulkDelete}
        title="Delete Email Templates?"
        message={`Are you sure you want to delete ${selectedRowIds.length} selected template(s)? This action cannot be undone.`}
        confirmRequiredText="delete"
        confirmLoading={isBulkDeleteInProgress}
      />

      <PreferencesModal
        open={preferencesModalOpen}
        onClose={() => setPreferencesModalOpen(false)}
        segmentRows={segmentRows}
        headerRow={headerRow}
        loading={segmentInventoriesLoading}
        error={segmentInventoriesError}
        canSavePreferences={canSavePreferences}
        expandedCell={expandedCell}
        vehiclesForExpandedCell={vehiclesForExpandedCell}
        vehiclesLoading={vehiclesForCellLoading}
        vehiclesError={vehiclesForCellError}
        preferredVinsForExpandedCell={preferredVinsForExpandedCell}
        missingVinsForExpandedCell={missingVinsForExpandedCell}
        onTogglePreferredVin={togglePreferredVin}
        onToggleExpanded={toggleExpanded}
        onSavePreferences={handleSavePreferences}
        saveLoading={preferencesSaveLoading}
      />

      {/* Generate Email Templates – progress card (Portal only, no Dialog/backdrop so app stays fully clickable) */}
      {showGenerateProgressPopup && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1300,
              minWidth: 520,
            }}
          >
            <EmailGenerationProgressCard
              isGeneratingEmail={showGenerateProgressPopup}
              streamingData={streamingDataForCard}
              currentSection={currentSectionForCard}
              onOpenEmailGenerationPopup={() => setShowSocketDetailsModal(true)}
            />
          </Box>
        </Portal>
      )}

      {/* Socket details modal – opened when user clicks progress card */}
      <Dialog
        open={showSocketDetailsModal}
        onClose={() => setShowSocketDetailsModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            maxHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Template generation
          </Typography>

          {/* Single status – latest socket message only; replaces on each new message */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              minHeight: 100,
            }}
          >
            {(() => {
              const latest = socketMessages.length > 0 ? socketMessages[socketMessages.length - 1] : null;
              const msg = latest?.data?.message;
              const total = msg?.total != null ? Number(msg.total) : null;
              const current = msg?.current != null ? Number(msg.current) : null;
              const status = msg?.status;
              const hasProgress = total != null && current != null && total > 0;
              const progressPct =
                total != null && current != null && total > 0
                  ? Math.round((current / total) * 100)
                  : null;

              // If no socket messages but generation in progress, show persisted state from localStorage/Redux
              if (!latest && !generationCompleted && isGenerateInProgress) {
                const persistedState = loadGenerationState(selectedDealerId);
                const lastPersistedMessage = persistedState?.lastSocketMessage;
                
                // Try to show last persisted socket message
                if (lastPersistedMessage) {
                  const persistedMsg = lastPersistedMessage?.data?.message;
                  const persistedTotal = persistedMsg?.total != null ? Number(persistedMsg.total) : progressFromSocket.total;
                  const persistedCurrent = persistedMsg?.current != null ? Number(persistedMsg.current) : progressFromSocket.current;
                  const persistedProgressPct = persistedTotal > 0 ? Math.round((persistedCurrent / persistedTotal) * 100) : null;
                  
                  return (
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                        {lastPersistedMessage.eventName?.replace(/_/g, ' ') ?? 'Template Generation'}
                      </Typography>
                      {persistedMsg?.status != null && (
                        <Typography variant="body2" color="text.secondary">
                          Status: {String(persistedMsg.status)}
                        </Typography>
                      )}
                      {persistedTotal != null && (
                        <Typography variant="body2" color="text.secondary">
                          Total: {persistedTotal}
                        </Typography>
                      )}
                      {persistedCurrent != null && (
                        <Typography variant="body2" color="text.secondary">
                          Current: {persistedCurrent}
                        </Typography>
                      )}
                      {persistedProgressPct != null && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Progress
                            </Typography>
                            <Typography variant="caption" fontWeight={600} color="primary.main">
                              {persistedProgressPct}%
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 6,
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${persistedProgressPct}%`,
                                height: '100%',
                                bgcolor: 'primary.main',
                                borderRadius: 1,
                                transition: 'width 0.2s ease',
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                      {persistedMsg?.dealer_id != null && (
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Dealer: {String(persistedMsg.dealer_id)}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                
                // Fallback: show progress from Redux if no persisted message
                if (progressFromSocket.total > 1 || progressFromSocket.current > 0) {
                  const fallbackProgressPct = Math.round((progressFromSocket.current / progressFromSocket.total) * 100);
                  return (
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Template Generation In Progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total: {progressFromSocket.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Current: {progressFromSocket.current}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="caption" fontWeight={600} color="primary.main">
                            {fallbackProgressPct}%
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            height: 6,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${fallbackProgressPct}%`,
                              height: '100%',
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                              transition: 'width 0.2s ease',
                            }}
                          />
                        </Box>
                      </Box>
                      {selectedDealerId && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Dealer: {selectedDealerId}
                        </Typography>
                      )}
                    </Box>
                  );
                }
                
                return (
                  <Typography variant="body2" color="text.secondary">
                    Waiting for socket events…
                  </Typography>
                );
              }

              if (generationCompleted && (!latest || latest.eventName === 'template_generation_completed')) {
                const m = latest?.data?.message;
                const generated = m?.generated != null ? Number(m.generated) : null;
                const skipped = m?.skipped != null ? Number(m.skipped) : null;
                const errors = formatGenerationErrors(m?.errors);
                return (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircleOutline sx={{ color: 'success.main' }} />
                      <Typography variant="body2" fontWeight={600}>
                        Template generation completed
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Email templates have been generated successfully.
                    </Typography>
                    {m?.dealer_id != null && (
                      <Typography variant="body2" color="text.secondary">
                        Dealer: {String(m.dealer_id)}
                      </Typography>
                    )}
                    {generated != null && (
                      <Typography variant="body2" color="text.secondary">
                        Generated: {generated}
                      </Typography>
                    )}
                    {skipped != null && (
                      <Typography variant="body2" color="text.secondary">
                        Skipped: {skipped}
                      </Typography>
                    )}
                    {errors.length > 0 && (
                      <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                        Errors: {errors.join(', ')}
                      </Typography>
                    )}
                  </Box>
                );
              }

              return (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                    {latest?.eventName?.replace(/_/g, ' ') ?? '—'}
                  </Typography>
                  {status != null && (
                    <Typography variant="body2" color="text.secondary">
                      Status: {String(status)}
                    </Typography>
                  )}
                  {total != null && (
                    <Typography variant="body2" color="text.secondary">
                      Total: {total}
                    </Typography>
                  )}
                  {current != null && (
                    <Typography variant="body2" color="text.secondary">
                      Current: {current}
                    </Typography>
                  )}
                  {hasProgress && progressPct != null && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color="primary.main">
                          {progressPct}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          bgcolor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${progressPct}%`,
                            height: '100%',
                            bgcolor: 'primary.main',
                            borderRadius: 1,
                            transition: 'width 0.2s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  {msg?.dealer_id != null && (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Dealer: {String(msg.dealer_id)}
                    </Typography>
                  )}
                  {msg?.generated != null && (
                    <Typography variant="body2" color="text.secondary">
                      Generated: {Number(msg.generated)}
                    </Typography>
                  )}
                  {msg?.skipped != null && (
                    <Typography variant="body2" color="text.secondary">
                      Skipped: {Number(msg.skipped)}
                    </Typography>
                  )}
                  {formatGenerationErrors(msg?.errors).length > 0 && (
                    <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                      Errors: {formatGenerationErrors(msg?.errors).join(', ')}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>

          {generationCompleted && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: 'success.light',
                color: 'success.dark',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircleOutline fontSize="medium" />
                <Typography variant="subtitle2" fontWeight={600}>
                  All done!
                </Typography>
              </Box>
              <Typography variant="body2">
                Email templates have been generated successfully.
              </Typography>
            </Box>
          )}

          {!generationCompleted && generateTemplatesLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Generating… API in progress
            </Typography>
          )}
        </Paper>
      </Dialog>

      {/* Standalone completion result modal – shown when generation completes; no dependency on progress card or datagrid */}
      <Dialog
        open={showCompletionResultModal}
        onClose={() => {
          setShowCompletionResultModal(false);
          clearGenerationState();
          dispatch(emailsGridActions.setGenerateProgress({ current: 0, total: 1 }));
          if (selectedDealerId) {
            clearLocalStorageGenerationState(selectedDealerId);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CheckCircleOutline sx={{ color: 'success.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              Template generation completed
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Email templates have been generated successfully.
          </Typography>
          {completionResult && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                mb: 2,
              }}
            >
              {completionResult.dealer_id != null && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Dealer: {completionResult.dealer_id}
                </Typography>
              )}
              {completionResult.generated != null && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Generated: {completionResult.generated}
                </Typography>
              )}
              {completionResult.skipped != null && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Skipped: {completionResult.skipped}
                </Typography>
              )}
              {Array.isArray(completionResult.errors) && completionResult.errors.length > 0 && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                  Errors: {completionResult.errors.join(', ')}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonMAIQ
              variant="contained"
              color="primary"
              onClick={() => {
                setShowCompletionResultModal(false);
                clearGenerationState();
                dispatch(emailsGridActions.setGenerateProgress({ current: 0, total: 1 }));
                if (selectedDealerId) {
                  clearLocalStorageGenerationState(selectedDealerId);
                }
              }}
            >
              OK
            </ButtonMAIQ>
          </Box>
        </Paper>
      </Dialog>
    </>
  );
}
